import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type { Profile } from "../types";
import { StockImageSuggestions } from "./StockImageSuggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import {
  mergeGabaritoComPublicacao,
  readGabaritoState,
  writeGabaritoState,
} from "./gabarito-utils";
import { profilesTrackingService } from "../../../services";
import {
  seoProfileApi,
  type GoogleConnection,
  type SeoCounts,
  type SeoJob,
  type SeoResearch,
  type SeoResearchItem,
} from "../../../services/seo";
import { jobBloqueiaEsteModo } from "./seo-publicacao";

type SeoTabProps = {
  profile: Profile;
};

type EditableItem = SeoResearchItem & {
  id: string;
  selected: boolean;
  confirmed: boolean;
  image?: {
    name: string;
    contentType: string;
    dataUrl: string;
  };
};

type Mode = "services" | "products" | "service_descriptions";

const Shell = styled.section`
  display: grid;
  gap: 18px;
  margin: 0 auto;
  max-width: 1180px;
`;

const Hero = styled.div`
  align-items: flex-start;
  background: #fff;
  border: 1px solid #dfe5ef;
  border-radius: 14px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 22px 24px;

  @media (max-width: 760px) {
    align-items: stretch;
    flex-direction: column;
    padding: 18px;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  gap: 14px;
`;

const HeroIcon = styled.span`
  align-items: center;
  background: #e8f0fe;
  border-radius: 12px;
  color: #1a73e8;
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
`;

const Title = styled.h3`
  color: #202124;
  font-size: 19px;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  color: #5f6368;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  max-width: 690px;
`;

const Button = styled.button<{ $secondary?: boolean }>`
  align-items: center;
  background: ${({ $secondary }) => ($secondary ? "#fff" : "#1a73e8")};
  border: 1px solid ${({ $secondary }) => ($secondary ? "#cbd5e1" : "#1a73e8")};
  border-radius: 9px;
  color: ${({ $secondary }) => ($secondary ? "#1a73e8" : "#fff")};
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 40px;
  padding: 9px 16px;
  transition: 0.18s ease;

  &:hover:not(:disabled) {
    background: ${({ $secondary }) => ($secondary ? "#f8fbff" : "#1765cc")};
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.18);
  }

  &:focus-visible {
    outline: 3px solid rgba(26, 115, 232, 0.24);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.52;
  }
`;

const GabaritoChip = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? "#137333" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#e6f4ea" : "#fff")};
  color: ${({ $active }) => ($active ? "#137333" : "#3c4043")};
  border-radius: 999px;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 12px;
  transition: 0.15s ease;
`;

const ApplyGabaritoButton = styled.button`
  align-items: center;
  background: #137333;
  border: 1px solid #137333;
  border-radius: 9px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  margin-top: 14px;
  min-height: 40px;
  padding: 9px 16px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &:hover:not(:disabled) {
    background: #0e5c28;
  }
`;

const LoginButton = styled(Button)`
  margin-top: 10px;
  width: 100%;
`;

const GoogleConnBox = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eef1f6;
  display: grid;
  gap: 6px;
  small {
    color: #5f6368;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .email {
    color: #202124;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status {
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    border-radius: 999px;
    padding: 3px 10px;
  }
  .status.ok {
    color: #137333;
    background: #e6f4ea;
  }
  .status.warn {
    color: #b06000;
    background: #fef7e0;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.55fr) minmax(280px, 0.75fr);

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #dfe5ef;
  border-radius: 14px;
  padding: 20px;
`;

const PanelTitle = styled.h4`
  align-items: center;
  color: #202124;
  display: flex;
  font-size: 15px;
  gap: 8px;
  margin: 0 0 14px;

  .material-icons {
    color: #1a73e8;
    font-size: 20px;
  }
`;

const ProfileFacts = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Fact = styled.div`
  background: #f8fafc;
  border-radius: 9px;
  min-width: 0;
  padding: 10px 12px;

  small {
    color: #64748b;
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    margin-bottom: 3px;
    text-transform: uppercase;
  }

  span {
    color: #263238;
    display: block;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Metrics = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, 1fr);
`;

const Metric = styled.div`
  background: #f8fafc;
  border: 1px solid #edf1f6;
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;

  strong {
    color: #1a73e8;
    display: block;
    font-size: 20px;
  }

  span {
    color: #64748b;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const Notice = styled.div<{ $tone?: "warning" | "error" | "success" }>`
  background: ${({ $tone }) =>
    $tone === "error" ? "#fef2f2" : $tone === "success" ? "#ecfdf5" : "#fffbeb"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "error" ? "#fecaca" : $tone === "success" ? "#a7f3d0" : "#fde68a"};
  border-radius: 10px;
  color: ${({ $tone }) =>
    $tone === "error" ? "#991b1b" : $tone === "success" ? "#065f46" : "#92400e"};
  font-size: 12px;
  line-height: 1.5;
  padding: 11px 13px;
`;

const Diagnostic = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  color: #1e3a5f;
  font-size: 13px;
  line-height: 1.55;
  padding: 13px 14px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 12px;
`;

const Chip = styled.span`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  font-size: 11px;
  padding: 5px 9px;
`;

const Toolbar = styled.div`
  align-items: end;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(210px, 1fr) auto;
  margin-bottom: 14px;

  @media (max-width: 620px) {
    align-items: stretch;
    grid-template-columns: 1fr;
  }
`;

const ToolbarActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
`;

const Field = styled.label`
  color: #475569;
  display: grid;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
  text-transform: uppercase;
`;

const Select = styled.select`
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  color: #1f2937;
  font: inherit;
  min-height: 40px;
  padding: 8px 10px;
`;

const ItemList = styled.div`
  display: grid;
  gap: 10px;
`;

const ItemCard = styled.article<{ $blocked?: boolean }>`
  background: ${({ $blocked }) => ($blocked ? "#fffaf0" : "#fff")};
  border: 1px solid ${({ $blocked }) => ($blocked ? "#f6c65b" : "#e2e8f0")};
  border-radius: 11px;
  display: grid;
  gap: 12px;
  grid-template-columns: 28px minmax(0, 1fr);
  padding: 13px;
`;

const ItemFields = styled.div`
  display: grid;
  gap: 9px;
  grid-template-columns: minmax(180px, 0.8fr) minmax(260px, 1.4fr);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  border: 1px solid #d6dde8;
  border-radius: 8px;
  box-sizing: border-box;
  color: #202124;
  font: inherit;
  min-height: 38px;
  padding: 8px 10px;
  width: 100%;
`;

const Textarea = styled.textarea`
  border: 1px solid #d6dde8;
  border-radius: 8px;
  box-sizing: border-box;
  color: #202124;
  font: inherit;
  min-height: 70px;
  padding: 8px 10px;
  resize: vertical;
  width: 100%;
`;

const MetaRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Badge = styled.span<{ $priority?: number }>`
  background: ${({ $priority }) =>
    $priority === 1 ? "#dcfce7" : $priority === 2 ? "#e8f0fe" : "#f1f5f9"};
  border-radius: 999px;
  color: ${({ $priority }) =>
    $priority === 1 ? "#166534" : $priority === 2 ? "#1d4ed8" : "#475569"};
  font-size: 10px;
  font-weight: 700;
  padding: 4px 8px;
  text-transform: uppercase;
`;

const ConfirmLabel = styled.label`
  align-items: flex-start;
  color: #854d0e;
  display: flex;
  font-size: 12px;
  gap: 7px;
  line-height: 1.4;
`;

const ProductPhoto = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #94a3b8;
  border-radius: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  padding: 12px;

  img {
    border-radius: 8px;
    height: 72px;
    object-fit: cover;
    width: 96px;
  }
`;

const PhotoPicker = styled.label`
  align-items: center;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #1a73e8;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  gap: 7px;
  min-height: 38px;
  padding: 0 12px;

  input {
    height: 1px;
    opacity: 0;
    position: absolute;
    width: 1px;
  }
`;

const PhotoMeta = styled.div`
  color: #475569;
  display: grid;
  font-size: 12px;
  gap: 3px;

  strong {
    color: #1f2937;
  }
`;

const PublishBar = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-top: 15px;
  padding: 12px;
`;

const ToggleLabel = styled.label`
  align-items: center;
  color: #334155;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
`;

const ProgressTrack = styled.div`
  background: #e2e8f0;
  border-radius: 999px;
  height: 9px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $value: number }>`
  background: #34a853;
  height: 100%;
  transition: width 0.25s ease;
  width: ${({ $value }) => `${$value}%`};
`;

const Log = styled.pre`
  background: #202124;
  border-radius: 10px;
  color: #c6f6d5;
  font: 11px/1.55 ui-monospace, SFMono-Regular, Consolas, monospace;
  margin: 12px 0 0;
  max-height: 170px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
`;

const Empty = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 11px;
  color: #64748b;
  font-size: 13px;
  padding: 28px 18px;
  text-align: center;
`;

const buildEditableItems = (result: SeoResearch): EditableItem[] => {
  const researchItems: EditableItem[] = result.itens.map((item, index) => ({
    ...item,
    id: `${item.uso}-${index}-${item.nome}`,
    selected: true,
    confirmed: !item.confirmar,
  }));
  const knownProducts = new Set(
    researchItems
      .filter((item) => item.uso === "produto")
      .map((item) => item.nome.trim().toLocaleLowerCase("pt-BR")),
  );
  const productItems: EditableItem[] = result.produtos
    .filter(
      (product) =>
        !knownProducts.has(product.nome.trim().toLocaleLowerCase("pt-BR")),
    )
    .map((product, index) => ({
      keyword: product.cobre[0] || product.nome,
      nome: product.nome,
      descricao: product.descricao,
      uso: "produto",
      intencao: "alta",
      prioridade: 2,
      confirmar: true,
      motivoConfirmar:
        "Confirme que este produto é vendido pelo perfil e selecione uma foto real.",
      id: `produto-extra-${index}-${product.nome}`,
      selected: true,
      confirmed: false,
    }));
  return [...researchItems, ...productItems];
};

const errorMessage = (error: unknown) => {
  const data = (
    error as {
      response?: { data?: { message?: string; userMessage?: string } };
    }
  )?.response?.data;
  return data?.userMessage || data?.message || (error instanceof Error ? error.message : "Erro inesperado.");
};

export const SeoTab = ({ profile }: SeoTabProps) => {
  const [research, setResearch] = useState<SeoResearch | null>(null);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [counts, setCounts] = useState<SeoCounts>({
    services: 0,
    products: 0,
    today: 0,
    dailyCap: 15,
  });
  const [mode, setMode] = useState<Mode>("services");
  const [dryRun, setDryRun] = useState(true);
  const [job, setJob] = useState<SeoJob | null>(null);
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [loadingPublish, setLoadingPublish] = useState(false);
  // ID do operador — precisa ser IGUAL ao "Seu ID de operador" da extensão do
  // Chrome. É por ele que o radar entrega o job pro navegador certo. Persistido.
  const [operatorId, setOperatorId] = useState(() =>
    typeof localStorage !== "undefined"
      ? localStorage.getItem("gr_operator_id") || ""
      : "",
  );
  const [feedback, setFeedback] = useState<{
    tone: "warning" | "error" | "success";
    message: string;
  } | null>(null);
  const [gabaritoKeywords, setGabaritoKeywords] = useState<string[]>([]);
  const [selectedGabarito, setSelectedGabarito] = useState<string[]>([]);
  const [googleConn, setGoogleConn] = useState<GoogleConnection | null>(null);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getCounts(profile.id)
      .then((value) => active && setCounts(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getGabaritoKeywords(profile.id)
      .then((value) => active && setGabaritoKeywords(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getGoogleConnection(profile.id)
      .then((value) => active && setGoogleConn(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getLatestResearch(profile.id)
      .then((result) => {
        if (!active || !result || !result.itens) return;
        setResearch(result);
        setItems(buildEditableItems(result));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    if (!job || !["QUEUED", "CLAIMED", "RUNNING"].includes(job.status)) return;
    const interval = window.setInterval(async () => {
      try {
        const next = await seoProfileApi.getStatus(job.jobId);
        setJob(next);
        if (["COMPLETED", "FAILED"].includes(next.status)) {
          window.clearInterval(interval);
          setLoadingPublish(false);
          setFeedback({
            tone: next.status === "COMPLETED" ? "success" : "error",
            message:
              next.status === "COMPLETED"
                ? next.dryRun
                  ? "Simulação concluída. Revise o log antes de publicar."
                  : "Atualização concluída."
                : next.error || "O job foi interrompido.",
          });
          const nextCounts = await seoProfileApi.getCounts(profile.id);
          setCounts(nextCounts);
        }
      } catch (error) {
        window.clearInterval(interval);
        setLoadingPublish(false);
        setFeedback({ tone: "error", message: errorMessage(error) });
      }
    }, 1500);
    return () => window.clearInterval(interval);
  }, [job, profile.id]);

  const visibleItems = useMemo(
    () =>
      items.filter((item) =>
        mode === "products" ? item.uso === "produto" : item.uso === "servico",
      ),
    [items, mode],
  );

  const selectedItems = visibleItems.filter((item) => item.selected);
  const hasBlocked = selectedItems.some(
    (item) => item.confirmar && !item.confirmed,
  );
  const hasProductWithoutPhoto =
    mode === "products" && selectedItems.some((item) => !item.image);
  // Só o job DESTA aba trava o botão: publicar serviços deixava produtos
  // travado até a extensão puxar a fila, e o operador tinha que refazer a
  // seleção inteira para conseguir publicar os produtos.
  const jobRunning = jobBloqueiaEsteModo(job, mode);
  const itemsReady =
    selectedItems.length > 0 &&
    !hasBlocked &&
    !hasProductWithoutPhoto &&
    !loadingPublish &&
    !jobRunning;
  // A publicação é feita pela EXTENSÃO do Chrome no navegador do operador que
  // gerencia o perfil (não pelo worker) — então não depende mais de worker online.
  const canPublish = itemsReady;

  const runResearch = async () => {
    setLoadingResearch(true);
    setFeedback(null);
    setJob(null);
    try {
      const result = await seoProfileApi.research(profile.id);
      setResearch(result);
      const built = buildEditableItems(result);
      setItems(built);
      setFeedback({
        tone: "success",
        message: `${built.length} oportunidades encontradas. Revise nomes, descrições e confirmações.`,
      });
    } catch (error) {
      setFeedback({ tone: "error", message: errorMessage(error) });
    } finally {
      setLoadingResearch(false);
    }
  };

  const updateItem = (id: string, patch: Partial<EditableItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const setProductPhoto = (id: string, file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFeedback({
        tone: "error",
        message: "Selecione um arquivo de imagem válido.",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") return;
      updateItem(id, {
        image: {
          name: file.name,
          contentType: file.type,
          dataUrl: reader.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  const addManualProduct = () => {
    const timestamp = Date.now();
    setItems((current) => [
      ...current,
      {
        keyword: "produto solar",
        nome: "Novo produto",
        descricao: "",
        uso: "produto",
        intencao: "alta",
        prioridade: 1,
        confirmar: true,
        motivoConfirmar:
          "Confirme que este produto é vendido pelo perfil e selecione uma foto real.",
        id: `produto-manual-${timestamp}`,
        selected: true,
        confirmed: false,
      },
    ]);
  };

  const toggleGabarito = (keyword: string) => {
    setSelectedGabarito((current) =>
      current.includes(keyword)
        ? current.filter((value) => value !== keyword)
        : [...current, keyword],
    );
  };

  const applyGabaritoToPlan = () => {
    if (!selectedGabarito.length) return;
    const uso: SeoResearchItem["uso"] =
      mode === "products" ? "produto" : "servico";
    const existing = new Set(
      items.map((item) => item.nome.trim().toLocaleLowerCase("pt-BR")),
    );
    const timestamp = Date.now();
    const isProduct = uso === "produto";
    const additions: EditableItem[] = selectedGabarito
      .filter(
        (keyword) =>
          !existing.has(keyword.trim().toLocaleLowerCase("pt-BR")),
      )
      .map((keyword, index) => ({
        keyword,
        nome: keyword,
        descricao: "",
        uso,
        intencao: "alta",
        prioridade: 1,
        confirmar: isProduct,
        motivoConfirmar: isProduct
          ? "Confirme que este produto é vendido pelo perfil e selecione uma foto real."
          : "",
        id: `gabarito-${timestamp}-${index}-${keyword}`,
        selected: true,
        confirmed: !isProduct,
      }));
    if (!additions.length) {
      setFeedback({
        tone: "warning",
        message: "As palavras selecionadas já estão no plano.",
      });
      return;
    }
    setItems((current) => [...current, ...additions]);
    setSelectedGabarito([]);
    setFeedback({
      tone: "success",
      message: `${additions.length} palavra(s) do Gabarito adicionada(s) ao plano.`,
    });
  };

  const publish = async () => {
    if (!canPublish) return;
    // O ID do operador é obrigatório também no Simular: o radar entrega o job à
    // extensão pelo operatorId. Sem ele, a extensão do operador não recebe nada.
    if (!operatorId.trim()) {
      setFeedback({
        tone: "error",
        message:
          "Informe o ID do operador (o mesmo configurado na extensão) antes de continuar.",
      });
      return;
    }
    setLoadingPublish(true);
    setFeedback(null);
    try {
      const nextJob = await seoProfileApi.publish({
        profileId: profile.id,
        mode,
        dryRun,
        operatorId: operatorId.trim(),
        items: selectedItems.map((item) => ({
          name: item.nome,
          description: item.descricao,
          requiresConfirmation: item.confirmar,
          confirmed: item.confirmed,
          imageName: item.image?.name,
          imageContentType: item.image?.contentType,
          imageDataUrl: item.image?.dataUrl,
        })),
      });
      setJob(nextJob);
      if (!dryRun) {
        try {
          const publicadas = selectedItems
            .map((item) => item.nome.trim())
            .filter(Boolean);
          if (publicadas.length) {
            const remote = await profilesTrackingService
              .getGabarito(profile.id)
              .catch(() => null);
            const atualizado = mergeGabaritoComPublicacao(
              remote,
              readGabaritoState(profile.id),
              publicadas,
            );
            writeGabaritoState(profile.id, atualizado);
            await profilesTrackingService.saveGabaritoText(profile.id, {
              postKeywords: atualizado.postKeywords,
              commentKeywords: atualizado.commentKeywords,
              postNotes: atualizado.postNotes,
              commentNotes: atualizado.commentNotes,
            });
            const refreshed = await seoProfileApi
              .getGabaritoKeywords(profile.id)
              .catch(() => null);
            if (refreshed) setGabaritoKeywords(refreshed);
          }
        } catch {
          setFeedback({
            tone: "warning",
            message:
              "Publicação enviada, mas não consegui gravar as palavras no Gabarito — confira a aba Gabarito.",
          });
        }
      }
    } catch (error) {
      setLoadingPublish(false);
      setFeedback({ tone: "error", message: errorMessage(error) });
    }
  };

  const progress = job?.total
    ? Math.round((job.done / job.total) * 100)
    : 0;

  return (
    <Shell>
      <Hero>
        <HeroCopy>
          <HeroIcon className="material-icons">travel_explore</HeroIcon>
          <div>
            <Title>
              SEO · Atualização inicial do perfil <FieldSyncBadge kind="backend" />
            </Title>
            <Subtitle>
              Pesquisa oportunidades, elimina duplicidades, pede confirmação
              para serviços duvidosos e prepara a publicação com limites de segurança.
            </Subtitle>
          </div>
        </HeroCopy>
        <Button
          aria-label="Pesquisar oportunidades com IA"
          type="button"
          onClick={runResearch}
          disabled={loadingResearch}
        >
          <span className="material-icons">auto_awesome</span>
          {loadingResearch
            ? "Pesquisando..."
            : "Pesquisar oportunidades com IA"}
        </Button>
      </Hero>

      <Grid>
        <Panel>
          <PanelTitle>
            <span className="material-icons">storefront</span>
            Perfil analisado
          </PanelTitle>
          <ProfileFacts>
            <Fact>
              <small>Empresa</small>
              <span>{profile.name}</span>
            </Fact>
            <Fact>
              <small>Segmento</small>
              <span>{profile.category || "Não informado"}</span>
            </Fact>
            <Fact>
              <small>Local</small>
              <span>
                {[profile.city, profile.state].filter(Boolean).join(" · ") ||
                  "Não informado"}
              </span>
            </Fact>
            <Fact>
              <small>Site</small>
              <span>{profile.website || "Não informado"}</span>
            </Fact>
          </ProfileFacts>
        </Panel>

        <Panel>
          <PanelTitle>
            <span className="material-icons">verified_user</span>
            Controle da automação
          </PanelTitle>
          <Metrics>
            <Metric>
              <strong>{counts.services}</strong>
              <span>Serviços</span>
            </Metric>
            <Metric>
              <strong>{counts.products}</strong>
              <span>Produtos</span>
            </Metric>
            <Metric>
              <strong>
                {counts.today}/{counts.dailyCap}
              </strong>
              <span>Hoje</span>
            </Metric>
          </Metrics>
          <Notice>
            Nome e categoria do negócio nunca são alterados. A publicação é feita
            pela extensão do Chrome, no navegador do operador que gerencia o perfil.
          </Notice>
          {googleConn && (
            <GoogleConnBox>
              <small>Conta Google do perfil (edição via integração)</small>
              <div className="row">
                <span className="email">
                  {googleConn.email || "não vinculada"}
                </span>
                <span
                  className={`status ${googleConn.connected ? "ok" : "warn"}`}
                >
                  {googleConn.connected
                    ? "Conectada"
                    : googleConn.hasToken
                      ? "Reconectar"
                      : "Não conectada"}
                </span>
              </div>
              {!googleConn.connected && (
                <LoginButton
                  $secondary
                  type="button"
                  onClick={() => seoProfileApi.reconnectGoogle()}
                >
                  <span className="material-icons">sync</span>
                  Reconectar conta Google
                </LoginButton>
              )}
            </GoogleConnBox>
          )}
        </Panel>
      </Grid>

      {feedback && <Notice $tone={feedback.tone}>{feedback.message}</Notice>}

      {research ? (
        <>
          <Panel>
            <PanelTitle>
              <span className="material-icons">insights</span>
              Diagnóstico da pesquisa
            </PanelTitle>
            <Diagnostic>{research.diagnostico}</Diagnostic>
            <ChipRow>
              {research.grupos.flatMap((group) =>
                group.keywords.slice(0, 8).map((keyword) => (
                  <Chip key={`${group.grupo}-${keyword}`}>{keyword}</Chip>
                )),
              )}
            </ChipRow>
          </Panel>

          {gabaritoKeywords.length > 0 && (
            <Panel>
              <PanelTitle>
                <span className="material-icons">bookmark</span>
                Palavras do Gabarito <FieldSyncBadge kind="backend" />
              </PanelTitle>
              <Subtitle>
                Selecione palavras do Gabarito e aplique como
                {mode === "products" ? " produtos" : " serviços"} no plano de
                atualização.
              </Subtitle>
              <ChipRow>
                {gabaritoKeywords.map((keyword) => (
                  <GabaritoChip
                    key={keyword}
                    type="button"
                    $active={selectedGabarito.includes(keyword)}
                    onClick={() => toggleGabarito(keyword)}
                  >
                    {keyword}
                  </GabaritoChip>
                ))}
              </ChipRow>
              <ApplyGabaritoButton
                type="button"
                onClick={applyGabaritoToPlan}
                disabled={selectedGabarito.length === 0}
              >
                <span className="material-icons">playlist_add</span>
                Aplicar {selectedGabarito.length} no plano
              </ApplyGabaritoButton>
            </Panel>
          )}

          <Panel>
            <Toolbar>
              <Field>
                Tipo de atualização
                <Select
                  value={mode}
                  onChange={(event) => setMode(event.target.value as Mode)}
                >
                  <option value="services">Cadastrar serviços</option>
                  <option value="service_descriptions">
                    Atualizar descrições de serviços
                  </option>
                  <option value="products">Preparar produtos</option>
                </Select>
              </Field>
              <ToolbarActions>
                {mode === "products" && (
                  <Button
                    $secondary
                    aria-label="Adicionar produto"
                    type="button"
                    onClick={addManualProduct}
                  >
                    <span className="material-icons">add</span>
                    Adicionar produto
                  </Button>
                )}
                <Badge $priority={1}>
                  {selectedItems.length} selecionado(s)
                </Badge>
              </ToolbarActions>
            </Toolbar>

            {visibleItems.length ? (
              <ItemList>
                {visibleItems.map((item) => {
                  const blocked = item.confirmar && !item.confirmed;
                  return (
                    <ItemCard key={item.id} $blocked={blocked}>
                      <input
                        aria-label={`Selecionar ${item.nome}`}
                        type="checkbox"
                        checked={item.selected}
                        onChange={(event) =>
                          updateItem(item.id, { selected: event.target.checked })
                        }
                      />
                      <div>
                        <MetaRow>
                          <Badge $priority={item.prioridade}>
                            Prioridade {item.prioridade}
                          </Badge>
                          <Badge>{item.intencao} intenção</Badge>
                          <Chip>{item.keyword}</Chip>
                        </MetaRow>
                        <ItemFields>
                          <Field>
                            Nome
                            <Input
                              value={item.nome}
                              maxLength={item.uso === "produto" ? 58 : 120}
                              onChange={(event) =>
                                updateItem(item.id, { nome: event.target.value })
                              }
                            />
                          </Field>
                          <Field>
                            Descrição
                            <Textarea
                              value={item.descricao}
                              maxLength={item.uso === "produto" ? 1000 : 300}
                              onChange={(event) =>
                                updateItem(item.id, {
                                  descricao: event.target.value,
                                })
                              }
                            />
                          </Field>
                        </ItemFields>
                        {item.uso === "produto" && (
                          <ProductPhoto>
                            {item.image && (
                              <img
                                src={item.image.dataUrl}
                                alt={`Prévia de ${item.nome}`}
                              />
                            )}
                            <PhotoPicker>
                              <span className="material-icons">
                                add_photo_alternate
                              </span>
                              {item.image ? "Trocar foto" : "Adicionar foto"}
                              <input
                                aria-label={`Foto do produto ${item.nome}`}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={(event) =>
                                  setProductPhoto(
                                    item.id,
                                    event.target.files?.[0],
                                  )
                                }
                              />
                            </PhotoPicker>
                            <PhotoMeta>
                              <strong>
                                {item.image?.name || "Foto obrigatória"}
                              </strong>
                              <span>
                                PNG ou JPG (o Google não aceita WebP). A foto será enviada junto do produto.
                              </span>
                            </PhotoMeta>
                          </ProductPhoto>
                        )}
                        {item.uso === "produto" && (
                          <StockImageSuggestions
                            keyword={item.nome}
                            city={profile.city}
                            locationHint={item.keyword}
                            onPick={(image) => updateItem(item.id, { image })}
                          />
                        )}
                        {item.confirmar && (
                          <ConfirmLabel>
                            <input
                              aria-label={`Confirmar que ${item.nome} é oferecido`}
                              type="checkbox"
                              checked={item.confirmed}
                              onChange={(event) =>
                                updateItem(item.id, {
                                  confirmed: event.target.checked,
                                })
                              }
                            />
                            <span>
                              Confirmo que o perfil realmente oferece este item.
                              {item.motivoConfirmar
                                ? ` ${item.motivoConfirmar}`
                                : ""}
                            </span>
                          </ConfirmLabel>
                        )}
                      </div>
                    </ItemCard>
                  );
                })}
              </ItemList>
            ) : (
              <Empty>
                A pesquisa não retornou itens para este tipo de atualização.
              </Empty>
            )}

            <PublishBar>
              <ToggleLabel>
                <input
                  aria-label="Simular"
                  type="checkbox"
                  checked={dryRun}
                  onChange={(event) => setDryRun(event.target.checked)}
                />
                Simular (não publica)
                <span>
                  {dryRun
                    ? "MARCADO: a extensão preenche o formulário mas NÃO publica. Desmarque para publicar de verdade."
                    : "DESMARCADO: vai publicar de verdade no Google ao clicar."}
                </span>
              </ToggleLabel>
              <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  fontSize: 12,
                  color: "#475569",
                  minWidth: 170,
                }}
              >
                ID do operador (o mesmo da extensão)
                <input
                  type="text"
                  value={operatorId}
                  onChange={(e) => {
                    setOperatorId(e.target.value);
                    try {
                      localStorage.setItem("gr_operator_id", e.target.value);
                    } catch {
                      /* ignore */
                    }
                  }}
                  placeholder="ex.: thiago"
                  style={{
                    padding: "6px 8px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                />
              </label>
              <Button
                aria-label={
                  dryRun ? "Simular atualização" : "Publicar atualização"
                }
                type="button"
                onClick={publish}
                disabled={!canPublish}
              >
                <span className="material-icons">
                  {dryRun ? "science" : "publish"}
                </span>
                {dryRun ? "Simular atualização" : "Publicar atualização"}
              </Button>
            </PublishBar>

            {hasBlocked && (
              <Notice>
                Confirme todos os itens destacados antes de iniciar a atualização.
              </Notice>
            )}
            {hasProductWithoutPhoto && (
              <Notice>Adicione uma foto para cada produto selecionado.</Notice>
            )}
          </Panel>
        </>
      ) : (
        <Empty>
          Clique em “Pesquisar oportunidades com IA” para iniciar a atualização
          deste perfil.
        </Empty>
      )}

      {job && (
        <Panel>
          <PanelTitle>
            <span className="material-icons">pending_actions</span>
            Progresso · {job.done}/{job.total}
          </PanelTitle>
          <ProgressTrack>
            <ProgressFill $value={progress} />
          </ProgressTrack>
          <Subtitle>
            {job.current
              ? `Processando: ${job.current}`
              : job.status === "COMPLETED"
                ? "Processo concluído."
                : "Preparando execução..."}
          </Subtitle>
          <Log>{job.log.join("\n") || "Aguardando o primeiro evento..."}</Log>
        </Panel>
      )}
    </Shell>
  );
};
